import { Dispatch, RefObject, SetStateAction, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Api from './API';
import { IError } from '../SmartForms/types';
import {
  ApplicationAction,
  IApplication,
  IAttachment,
  IEmailTemplate,
  ITemplateAttachment,
  ITimestamp,
  IVideoInterview,
  ICognologyRecord
} from './types';
import { IEvent } from '../Job/types';
import { secondsToTimeString, timeStringToSeconds } from './config';

export function useGetEmailTemplate({
  selectedEmailTemplate,
  setBody,
  dispatch,
  setSubject,
  setTemplateAttachments,
  endpoint
}: {
  selectedEmailTemplate: string | null;
  setBody: Dispatch<SetStateAction<string>>;
  dispatch: Dispatch<ApplicationAction>;
  setSubject?: Dispatch<SetStateAction<string>>;
  setTemplateAttachments?: Dispatch<SetStateAction<ITemplateAttachment[]>>;
  endpoint?: Promise<{ res: IEmailTemplate }>;
}) {
  useQuery({
    queryKey: [selectedEmailTemplate],
    queryFn: async () => {
      if (!selectedEmailTemplate) return;
      const { res } = await (endpoint ||
        Api.getEmailTemplateFromCalendar(Number(selectedEmailTemplate.split(' -')[0])));
      return res;
    },
    onSuccess: (emailTemplate: IEmailTemplate) => {
      setSubject && setSubject(emailTemplate.subject);
      setTemplateAttachments && setTemplateAttachments(emailTemplate.email_attachments);
      setBody(emailTemplate.body);
    },
    onError: (error: IError) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: error.errors.join(', '), state: 'error' }
      }),
    enabled: !!selectedEmailTemplate
  });
}

export function useGetEvents({
  dispatch,
  eventTypeId,
  enabled = true
}: {
  dispatch: Dispatch<ApplicationAction>;
  eventTypeId?: number;
  enabled?: boolean;
}) {
  const { data: events, isLoading: loadingEvents } = useQuery({
    queryKey: ['events', eventTypeId],
    queryFn: async () => {
      const { res } = await Api.getEvents({
        date: new Date().toISOString(),
        override_limit: true,
        event_type_id: eventTypeId
      });
      return res.events as IEvent[];
    },
    onError: (error: IError) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: error.errors.join(', '), state: 'error' }
      }),
    enabled
  });

  return { events, loadingEvents };
}

export function useGetIntegrationsData({
  applicationId,
  dispatch
}: {
  applicationId?: number;
  dispatch: Dispatch<ApplicationAction>;
}) {
  const {
    data: integrationsData,
    refetch: refetchIntegrationsData,
    isRefetching: refetchingIntegrationsData,
    isLoading: loadingIntegrationsData
  } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      if (applicationId) {
        const { res } = await Api.getIntegrationsData(applicationId);
        return res;
      }
    },
    onError: (error) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting integrations data, ${error}`,
          state: 'error'
        }
      });
    }
  });

  return {
    integrationsData,
    refetchIntegrationsData,
    refetchingIntegrationsData,
    loadingIntegrationsData
  };
}

export function useGetCognologyData({
  applicationId,
  dispatch,
  setRecord,
  setCategories,
  setAttachments,
  enabled = true
}: {
  applicationId?: number;
  dispatch: Dispatch<ApplicationAction>;
  setRecord?: Dispatch<SetStateAction<ICognologyRecord | null>>;
  setCategories?: Dispatch<SetStateAction<Record<string, [string, number] | null> | null>>;
  setAttachments?: Dispatch<
    SetStateAction<Record<number, { id: number; name: string } | null> | null>
  >;
  enabled?: boolean;
}) {
  const { data: cognologyData, isLoading: loadingCognologyData } = useQuery({
    queryKey: ['cognology data'],
    queryFn: async () => {
      if (!applicationId) return;
      const { res } = await Api.getCognologyData(applicationId);
      return res;
    },
    onSuccess: (res) => {
      if (res && setRecord && setCategories && setAttachments) {
        res.record.candidate_gender === 'Unspecified'
          ? setRecord({ ...res.record, candidate_gender: null })
          : setRecord(res.record);
        if (res.record.categories) {
          Object.keys(res.record.categories).forEach((key) => {
            let savedCategoryValue: [string, number] | null = null;
            const savedCategory = res.record.categories?.[key];
            if (savedCategory) {
              savedCategoryValue = [
                savedCategory.name || savedCategory.new_value || '',
                savedCategory.id || 0
              ];
            }
            setCategories((prev) => ({ ...prev, [key]: savedCategoryValue }));
          });
        } else {
          res.categories.forEach((category) => {
            setCategories((prev) => ({ ...prev, [category.id]: null }));
          });
        }
        res.record.assets
          ? setAttachments(res.record.assets)
          : res.application_assets.forEach((asset) => {
              setAttachments((prev) => ({ ...prev, [asset.id]: null }));
            });
      }
    },
    onError: (error) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `There was an error getting cognology data, ${error}`, state: 'error' }
      }),
    enabled: enabled
  });

  return { cognologyData, loadingCognologyData };
}

export function useGetSmsTemplatePlaceholders({
  kind,
  dispatch
}: {
  kind: string;
  dispatch: Dispatch<ApplicationAction>;
}) {
  const { data: placeholders } = useQuery({
    queryKey: ['sms placeholders'],
    queryFn: async () => {
      const { res } = await Api.getSmsTemplatePlaceholders(kind);
      return res.placeholders;
    },
    onError: (error: IError) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: error.errors.join(', '), state: 'error' }
      }),
    initialData: []
  });

  return { placeholders };
}

export function useGetAssignableUsers({ dispatch }: { dispatch: Dispatch<ApplicationAction> }) {
  const { data: assignableUsers, isLoading: loadingAssignableUsers } = useQuery({
    queryKey: ['assignable users'],
    queryFn: async () => {
      const { res } = await Api.getAssignableUsers();
      return res.assignable_users;
    },
    onError: (error) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting assignable users data, ${error}`,
          state: 'error'
        }
      })
  });

  return { assignableUsers, loadingAssignableUsers };
}

export function useFormQuestions({
  application,
  selectedFormId,
  dispatch,
  formResponseId
}: {
  application: IApplication | undefined;
  selectedFormId: number | undefined;
  dispatch: Dispatch<ApplicationAction>;
  formResponseId: number | undefined;
}) {
  const { data: formQuestions, isLoading: loadingFormQuestions } = useQuery({
    queryKey: ['formQuestions', selectedFormId, formResponseId],
    queryFn: async () => {
      if (!application || !selectedFormId) return;
      const { res } = await Api.getFormQuestions({
        form_id: selectedFormId,
        form_response_id: formResponseId
      });
      return res.fields;
    },
    onError: (error) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `There was an error getting form questions, ${error}`, state: 'error' }
      }),
    enabled: !!application && !!selectedFormId && !!formResponseId
  });

  return { formQuestions, loadingFormQuestions };
}

export function useFormResponse({
  FormResponseId,
  dispatch,
  application
}: {
  FormResponseId: number;
  dispatch: Dispatch<ApplicationAction>;
  application?: IApplication;
}) {
  return useQuery({
    queryKey: ['formQuestionAndAnswers', FormResponseId],
    queryFn: async () => {
      if (!application) return;
      const { res } = await Api.showFormResponse(
        application.job.id,
        application.id,
        FormResponseId
      );
      return res.question_and_answers;
    },
    onError: (error) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `There was an error getting form response, ${error}`, state: 'error' }
      }),
    enabled: !!application
  });
}

export function useTimestampData({
  selectedAttachment,
  dispatch,
  listRef,
  application,
  newTimestamp,
  setNewTimestamp
}: {
  selectedAttachment: IAttachment | IVideoInterview;
  dispatch: Dispatch<ApplicationAction>;
  listRef: RefObject<HTMLDivElement>;
  application: IApplication | undefined;
  newTimestamp: ITimestamp;
  setNewTimestamp: Dispatch<SetStateAction<ITimestamp>>;
}) {
  const queryClient = useQueryClient();
  const [timestampAdded, setTimestampAdded] = useState<boolean>(false);
  const isVideoInterview = 'watch_interview_link' in selectedAttachment;
  const fetchTimestampsAPI = () => {
    if (selectedAttachment?.id && application && !isVideoInterview) {
      return Api.getVideoTimestamps(selectedAttachment.id);
    }
    if (isVideoInterview && application) {
      return Api.getVideoInterviewBookmarks(application.id);
    }
  };

  const { data: timestampData, isLoading: loadingTimestampData } = useQuery({
    queryKey: ['timestamp_data', selectedAttachment],
    queryFn: async () => {
      if (fetchTimestampsAPI) {
        const response = await fetchTimestampsAPI();
        if (response) {
          const { res } = response;
          if (isVideoInterview && 'bookmarks' in res) {
            // sanitize the timestamps in 00:00:00 format
            res.bookmarks = res.bookmarks.map((bookmark) => ({
              ...bookmark,
              value: secondsToTimeString(bookmark.value as number)
            }));
          }
          return res;
        }
      }
    },
    onSettled: () => {
      if (timestampAdded) {
        listRef.current?.scrollTo(0, listRef.current.scrollHeight);
        setTimestampAdded(false);
      }
    },
    onError: (error: { message: string }) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `There was an error getting timestamp data, ${error}`, state: 'error' }
      }),
    enabled:
      (!isVideoInterview && selectedAttachment?.attached_content_type?.includes('video')) ||
      isVideoInterview
  });

  const updateTimestampApi = () => {
    if (!selectedAttachment || !timestampData || !newTimestamp.id || !application) return;
    if (isVideoInterview) {
      return Api.updateVideoInterviewBookmark(
        application.id,
        newTimestamp.id as string,
        newTimestamp.name,
        timeStringToSeconds(newTimestamp.value as string)
      );
    } else {
      return Api.updateVideoTimestamp(
        selectedAttachment.id,
        newTimestamp.id as number,
        newTimestamp.name,
        newTimestamp.value as string
      );
    }
  };

  const { mutate: updateTimestamp, isLoading: updatingTimestamp } = useMutation({
    mutationFn: async () => {
      const { res } = await updateTimestampApi();
      return res;
    },
    onSuccess: async () => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Timestamp updated successfully', state: 'success' }
      });
      setNewTimestamp({ id: null, name: '', value: '' });
      await queryClient.invalidateQueries(['timestamp_data', selectedAttachment]);
    },
    onError: (error) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `Error updating task: ${error}`, state: 'error' }
      });
    }
  });

  const createTimestampApi = () => {
    if (!selectedAttachment || !timestampData || !application) return;
    if (isVideoInterview) {
      return Api.createVideoInterviewBookmark(
        application.id,
        newTimestamp.name,
        timeStringToSeconds(newTimestamp.value as string)
      );
    } else {
      return Api.createVideoTimestamp(
        selectedAttachment.id,
        newTimestamp.name,
        newTimestamp.value as string
      );
    }
  };
  const { mutate: addTimestamp } = useMutation({
    mutationFn: async () => {
      const { res } = await createTimestampApi();
      return res;
    },
    onSuccess: async () => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Timestamp added successfully', state: 'success' }
      });
      setNewTimestamp({ ...newTimestamp, name: '', value: '' });
      dispatch({ type: 'RESET_TIMESTAMP_STATE' });
      await queryClient.invalidateQueries(['timestamp_data', selectedAttachment]);
      setTimestampAdded(true);
    },
    onError: (error) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `Error adding timestamp: ${error}`, state: 'error' }
      });
    }
  });

  const deleteTimestampApi = (id: number | string) => {
    if (!selectedAttachment || !timestampData || !application) return;
    if (isVideoInterview) {
      return Api.deleteVideoInterviewBookmark(application?.id, id as string);
    } else {
      return Api.deleteVideoTimestamp(selectedAttachment.id, id as number);
    }
  };

  const { mutate: deleteTimestamp } = useMutation({
    mutationFn: async (id: number | string) => {
      deleteTimestampApi(id);
    },
    onSuccess: async () => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Timestamp deleted successfully', state: 'success' }
      });
      await queryClient.invalidateQueries(['timestamp_data', selectedAttachment]);
    },
    onError: (error) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `Error deleting timestamp: ${error}`, state: 'error' }
      });
    }
  });
  return {
    timestampData,
    loadingTimestampData,
    updateTimestamp,
    updatingTimestamp,
    addTimestamp,
    deleteTimestamp
  };
}

export function useCurrentTime(
  videoRef: RefObject<HTMLVideoElement>,
  setCurrentTime: Dispatch<SetStateAction<string>>
) {
  useEffect(() => {
    const videoElement = videoRef.current;
    const getCurrentTime = () => {
      if (videoRef.current) {
        setCurrentTime(secondsToTimeString(videoRef.current.currentTime));
      }
    };
    if (videoElement) {
      videoElement.addEventListener('timeupdate', getCurrentTime);
      videoElement.addEventListener('pause', getCurrentTime);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('timeupdate', getCurrentTime);
        videoElement.removeEventListener('pause', getCurrentTime);
      }
    };
  }, [videoRef]);
}

export const useNotepadUpdater = ({
  application,
  dispatch
}: {
  application?: IApplication;
  dispatch: Dispatch<ApplicationAction>;
}) => {
  const queryClient = useQueryClient();

  const { isLoading, mutate } = useMutation({
    mutationFn: async (note: string) => {
      if (application) {
        const { res } = await Api.updateApplicationNotepad(
          application.job.id,
          application.id,
          note
        );
        return res;
      }
    },
    onSuccess: () => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Notepad updated successfully', state: 'success' }
      });
      dispatch({
        type: 'SET_OVERVIEW_TAB',
        payload: { hasUnsavedChanges: false }
      });
      queryClient.invalidateQueries(['application'], { exact: true });
    },
    onError: (error: { error: string }) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error in updating application notepad, ${error.error}`,
          state: 'error'
        }
      });
    }
  });

  return {
    isLoading,
    mutate
  };
};
